import { CommandType, Monster } from '../types';

type CommandModifiers = Record<CommandType, number>;

/**
 * 相対比較のスケール係数
 * ステータス差100につき0.4の変化（1.0 ± 0.4）
 */
const MODIFIER_SCALE = 0.004;

const MIN_MODIFIER = 0.4;
const MAX_MODIFIER = 2.0;

function clampModifier(value: number): number {
  return Math.max(MIN_MODIFIER, Math.min(MAX_MODIFIER, value));
}

/**
 * 相手モンスターとの相対比較に基づく重みモディファイア
 *
 * 固定ベースラインではなく自分と相手のステータス差で判断するため、
 * レベルやパラメータ調整に依存せず、自然な個性の違いが生まれる。
 *
 * 判断軸:
 * - 総合戦闘力差 = (自分strength+toughness) - (相手strength+toughness)  (+なら殴り合いで有利)
 * - 特殊優劣 = 自分special  - 相手special    (+なら特殊交換で有利)
 * - 物理脅威 = 相手strength - 自分strength   (+なら近づくと危ない)
 *
 * @param self 自分のモンスター
 * @param opponent 相手モンスター
 */
export function getOpponentAbilityModifiers(self: Monster, opponent: Monster): CommandModifiers {
  const battleBalance  = (self.stats.strength + self.stats.toughness)
                       - (opponent.stats.strength + opponent.stats.toughness); // + = 総合戦闘力で上回る
  const specialBalance = self.stats.special - opponent.stats.special;          // + = 特殊交換有利
  const physicalThreat = opponent.stats.strength - self.stats.strength;        // + = 相手物理が上

  return {
    // 総合戦闘力で上回るほど前進して殴る。相手物理が上なら近づきすぎない
    [CommandType.ADVANCE]: clampModifier(
      1.0 + MODIFIER_SCALE * battleBalance
          - MODIFIER_SCALE * 0.5 * physicalThreat
    ),
    // 相手物理が上なら距離を取る。総合戦闘力で勝るなら近距離を維持
    [CommandType.RETREAT]: clampModifier(
      1.0 + MODIFIER_SCALE * 0.5 * physicalThreat
          - MODIFIER_SCALE * 0.5 * battleBalance
    ),
    // 総合戦闘力で上回るほど武器攻撃が有効
    [CommandType.WEAPON_ATTACK]: clampModifier(
      1.0 + MODIFIER_SCALE * battleBalance
    ),
    // 特殊交換有利なら積極的に。総合戦闘力で劣る・相手物理が強い場合も特殊に切替
    [CommandType.SPECIAL_ATTACK]: clampModifier(
      1.0 + MODIFIER_SCALE * specialBalance
          + MODIFIER_SCALE * Math.max(0, -battleBalance)
          + MODIFIER_SCALE * 0.5 * Math.max(0, physicalThreat)
    ),
    // 相手の特殊が自分より強いほどREFLECTOR有効
    [CommandType.REFLECTOR]: clampModifier(
      1.0 - MODIFIER_SCALE * specialBalance
    ),
    [CommandType.STANCE_A]: 1.0,
    [CommandType.STANCE_B]: 1.0,
  };
}
